import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Truck, MapPin, Package, Mail } from 'lucide-react';
import Navigation from '../components/Navigation';
import Footer from '../components/Footer';

const ShippingPage = () => {
  return (
    <div className="min-h-screen bg-bone-white">
      <Helmet>
        <title>Shipping & Delivery | The Perfect Peach Club</title>
        <meta name="description" content="Shipping information for The Perfect Peach Club. Nationwide delivery, West Rand local delivery, discreet packaging, and tracking details." />
      </Helmet>

      <Navigation />

      <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-32">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="font-playfair text-4xl sm:text-5xl font-semibold text-[#2D2420] mb-4">
            Shipping & Delivery
          </h1>
          <p className="text-lg text-african-earth leading-relaxed">
            At The Perfect Peach Club, every order is packed with care and intention.
            Your products should arrive safely, discreetly, and without stress.
          </p>
        </div>

        <div className="space-y-10">
          {/* Processing Time */}
          <section className="bg-white rounded-2xl p-6 sm:p-8 shadow-soft">
            <h2 className="font-playfair text-2xl font-semibold text-[#2D2420] mb-3">
              Processing Time
            </h2>
            <p className="text-african-earth leading-relaxed">
              Orders are packed within 1–2 business days. You'll receive an email once your parcel has been collected.
            </p>
          </section>

          {/* Nationwide Delivery */}
          <section className="bg-white rounded-2xl p-6 sm:p-8 shadow-soft">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-perfect-peach/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Truck className="w-5 h-5 text-perfect-peach" />
              </div>
              <div>
                <h2 className="font-playfair text-2xl font-semibold text-[#2D2420] mb-3">
                  Nationwide Delivery
                </h2>
                <div className="space-y-2 mb-4">
                  <p className="text-[#2D2420] font-medium">Flat rate: R99</p>
                  <p className="text-perfect-peach font-medium">FREE shipping on orders over R600</p>
                </div>
                <p className="text-african-earth leading-relaxed">
                  Estimated delivery: 1–3 business days (major cities) | 2–5 days (outlying areas)
                </p>
              </div>
            </div>
          </section>

          {/* Local Delivery */}
          <section className="bg-white rounded-2xl p-6 sm:p-8 shadow-soft">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-[#E8EDE8] rounded-full flex items-center justify-center flex-shrink-0">
                <MapPin className="w-5 h-5 text-[#6B7D6B]" />
              </div>
              <div>
                <h2 className="font-playfair text-2xl font-semibold text-[#2D2420] mb-3">
                  Local Delivery (West Rand)
                </h2>
                <p className="text-[#2D2420] font-medium mb-4">R49 local delivery</p>
                <p className="text-african-earth leading-relaxed">
                  If you're located in the West Rand, we offer faster hand delivery.
                  This helps reduce costs and environmental impact. Most local orders arrive within 24–48 hours.
                </p>
              </div>
            </div>
          </section>

          {/* Packaging & Privacy */}
          <section className="bg-white rounded-2xl p-6 sm:p-8 shadow-soft">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 bg-perfect-peach/10 rounded-full flex items-center justify-center flex-shrink-0">
                <Package className="w-5 h-5 text-perfect-peach" />
              </div>
              <div>
                <h2 className="font-playfair text-2xl font-semibold text-[#2D2420] mb-4">
                  Packaging & Privacy
                </h2>
                <ul className="space-y-2 text-african-earth">
                  <li className="flex items-center gap-2">
                    <span className="text-perfect-peach">✓</span>
                    Discreet outer packaging
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-perfect-peach">✓</span>
                    No product details visible
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="text-perfect-peach">✓</span>
                    Securely sealed parcels
                  </li>
                </ul>
              </div>
            </div>
          </section>

          {/* Tracking */}
          <section className="bg-white rounded-2xl p-6 sm:p-8 shadow-soft">
            <h2 className="font-playfair text-2xl font-semibold text-[#2D2420] mb-3">
              Tracking
            </h2>
            <p className="text-african-earth leading-relaxed">
              You'll receive a tracking link once your order is collected.
            </p>
          </section>

          {/* Questions */}
          <section className="bg-bone-white border border-peach-light/30 rounded-2xl p-6 sm:p-8 text-center">
            <div className="flex justify-center mb-4">
              <div className="w-12 h-12 bg-perfect-peach/10 rounded-full flex items-center justify-center">
                <Mail className="w-6 h-6 text-perfect-peach" />
              </div>
            </div>
            <h2 className="font-playfair text-2xl font-semibold text-[#2D2420] mb-3">
              Questions?
            </h2>
            <p className="text-african-earth">
              Email us anytime at{' '}
              <a 
                href="mailto:info@theperfectpeachclub.co.za" 
                className="text-perfect-peach hover:underline"
              >
                info@theperfectpeachclub.co.za
              </a>
            </p>
          </section>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ShippingPage;
